import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type {
  Vendor,
  CreateVendorRequest,
  UpdateVendorRequest
} from "@/types/vendors";

export function useVendors() {
  const queryClient = useQueryClient();

  // Fetch all vendors using CMS API
  const {
    data: vendors = [],
    isLoading,
    error
  } = useQuery<Vendor[]>({
    queryKey: ["/marketplace/vendors"],
    queryFn: async () => {
      return await apiRequest("GET", "/marketplace/vendors");
    }
  });

  // Create vendor mutation using CMS API
  const createVendorMutation = useMutation({
    mutationFn: async (vendorData: CreateVendorRequest) => {
      return await apiRequest("POST", "/marketplace/vendors", vendorData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/marketplace/vendors"] });
    }
  });

  // Update vendor mutation using CMS API
  const updateVendorMutation = useMutation({
    mutationFn: async ({ id, ...data }: UpdateVendorRequest & { id: string }) => {
      return await apiRequest("PUT", `/marketplace/vendors/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/marketplace/vendors"] });
    }
  });

  return {
    vendors,
    isLoading,
    error,
    createVendor: createVendorMutation.mutate,
    updateVendor: updateVendorMutation.mutate,
    isCreating: createVendorMutation.isPending,
    isUpdating: updateVendorMutation.isPending
  };
}
